/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Column, Bar } from '@ant-design/plots';
import { SectionCard, StatCard, ChartCard, DataTable } from '../../ui';
import { CHART_SEMANTIC, CHART_AXIS } from '../../ui/chartPalette';
import {
  boardStoreVisit,
  boardStoreDevice,
  boardStoreDeviceAssets,
} from '@/services/athenaBoard';
import { visitColumns } from './columns/storeVisitColumns';
import { storeDeviceColumns } from './columns/storeDeviceColumns';
import styles from './board.less';

/**
 * 门店运营 —— 2026-09-04 结构更新版
 * ==================================================================
 * ⚠️ 本轮仍只搭结构、不做视觉：区块与字段名来自商家端截图与框架文档，
 * 配色 / 图表类型待设计规格或该页源码补齐后逐块校正，已标「待确认」。
 *
 * 相比上一版的变化（按框架文档）：
 *   · 「会员活跃趋势 / 到店频次分层 / 会员活跃 TOP / 打卡行为洞察」已移出
 *     本 tab，归入 **会员经营 → 会员行为**（见 MemberOps.jsx）
 *   · 本 tab 收敛为两块：到店运营、设备与器械
 *   · 「器械使用热度 / 器械使用时间统计」改名为「**设备**使用热度 TOP6 /
 *     **设备**使用时间统计」
 *
 * 数据仍来自同一个 B 端 payload（boardStoreVisit），本 tab 只渲染到店与
 * 设备两类字段；被移走的字段由「会员行为」从同一接口取。
 */

const StoreOps = (props) => {
  const { tenantId, coachUserId } = props;
  const [visit, setVisit] = useState({});
  const [device, setDevice] = useState({});
  const [visitLoading, setVisitLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [visitError, setVisitError] = useState('');
  const [deviceError, setDeviceError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [assetPage, setAssetPage] = useState({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    data: [],
  });

  useEffect(() => {
    if (!tenantId || !coachUserId) return;
    const params = { tenantId, coachUserId };
    setVisitLoading(true);
    setDeviceLoading(true);
    setVisitError('');
    setDeviceError('');
    boardStoreVisit(params)
      .then((r) => {
        if (r && r.code === '00000') setVisit(r.data || {});
        else throw new Error((r && r.msg) || '到店运营加载失败');
      })
      .catch((error) => setVisitError(error.message))
      .finally(() => setVisitLoading(false));
    boardStoreDevice(params)
      .then((r) => {
        if (!r || r.code !== '00000') {
          throw new Error((r && r.msg) || '设备与器械加载失败');
        }
        const nextDevice = r.data || {};
        setDevice(nextDevice);

        /* storeDevice.asset.deviceTotal 是当前商家的设备总数，资产明细按
           这个数量取全量，不能固定只取首页 10 条。 */
        const total = Number(nextDevice.asset?.deviceTotal) || 0;
        if (!total) {
          setAssetPage((current) => ({
            ...current,
            currentPage: 1,
            total: 0,
            data: [],
          }));
          return;
        }
        return boardStoreDeviceAssets({
          ...params,
          currentPage: 1,
          pageSize: total,
        }).then((assetResponse) => {
          if (!assetResponse || assetResponse.code !== '00000') {
            throw new Error(
              (assetResponse && assetResponse.msg) || '设备资产明细加载失败',
            );
          }
          const assetData = assetResponse.data || {};
          const assetTotal =
            assetData.total == null ? total : Number(assetData.total);
          setAssetPage((current) => ({
            ...current,
            currentPage: 1,
            pageSize: assetTotal,
            total: assetTotal,
            data: Array.isArray(assetData.list) ? assetData.list : [],
          }));
        });
      })
      .catch((error) => setDeviceError(error.message))
      .finally(() => setDeviceLoading(false));
  }, [tenantId, coachUserId, retryKey]);

  const kpi = visit.kpi || {};
  const realtime = visit.realtime || {};
  const asset = device.asset || {};
  const period = visit.periodDistribution || [];
  const hourUsage = device.hourUsage || {};

  return (
    <div className={styles.boardBody}>
      {/* ---------------- 到店运营（B 端页面） ---------------- */}
      <SectionCard
        title="到店运营"
        subtitle="B 端口径 · 今日与本月到店 / 线上打卡"
        loading={visitLoading}
        error={visitError}
        onRetry={() => setRetryKey((current) => current + 1)}
      >
        <div className={styles.kpiRow}>
          <StatCard
            title="今日到店人数"
            value={kpi.todayVisitNum}
            unit="人"
            hint={kpi.todayVisitHint}
          />
          <StatCard
            title="今日线上打卡"
            value={kpi.todayOnlineClockNum}
            unit="人"
            hint={kpi.todayOnlineClockHint}
          />
          <StatCard
            title="本月到店会员"
            value={kpi.monthVisitUserNum}
            unit="人"
            hint={kpi.monthVisitUserHint}
          />
          <StatCard
            title="本月线上打卡"
            value={kpi.monthOnlineClockNum}
            unit="人"
            hint={kpi.monthOnlineClockHint}
          />
        </div>
        <div className={`${styles.kpiRow} ${styles.kpiRow2}`}>
          <StatCard
            title="今日高峰时段"
            value={realtime.peakPeriod}
            hint={realtime.peakTip}
          />
          <StatCard
            title="今日打卡率"
            value={realtime.clockRate}
            unit="%"
            tips={realtime.clockRateHint}
          />
        </div>
      </SectionCard>

      <SectionCard title="到店时段分布" subtitle="近 30 天到店行为次数">
        <ChartCard height={300} empty={!period.length}>
          <Column
            height={300}
            data={period}
            xField="name"
            yField="value"
            legend={false}
            /* 只有 6 个分桶，不限宽会被拉到 150px+ 一根，很笨重 */
            maxColumnWidth={56}
            color={CHART_SEMANTIC.primary}
            columnStyle={{ radius: [3, 3, 0, 0] }}
            label={{
              position: 'top',
              style: { fill: CHART_AXIS.valueLabel, fontSize: 12 },
            }}
            yAxis={{
              /* 上界等于最大值时，最高柱子的顶部数值标签会被画布裁掉，
                 预留 15% 并向上取整到 10 的倍数 */
              max:
                Math.ceil(
                  (Math.max(...period.map((i) => Number(i.value) || 0), 0) *
                    1.15) /
                    10,
                ) * 10,
              grid: {
                line: { style: { lineDash: [4, 4], stroke: CHART_AXIS.grid } },
              },
            }}
            xAxis={{
              line: { style: { stroke: CHART_AXIS.axisLine } },
              tickLine: { style: { stroke: CHART_AXIS.axisLine } },
              label: { autoHide: false, style: { fill: CHART_AXIS.axisLabel } },
            }}
          />
        </ChartCard>
      </SectionCard>

      <SectionCard title="今日到店用户" subtitle="T+0 实时明细">
        <DataTable
          rowKey="userId"
          size="small"
          columns={visitColumns}
          /* VO 里 todayUsers 就是数组（BoardStoreVisitVO.todayUsers: List<TodayUser>），
             此前误按 { list, emptyText } 对象读 → .list 恒 undefined，
             后端有数据表格也永远空（与 H5 对不上数据的根因，2026-09-20 修复） */
          dataSource={visit.todayUsers || []}
          emptyText="今日暂无到店记录"
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      </SectionCard>

      {/* ---------------- 设备与器械（B 端页面） ---------------- */}
      <SectionCard
        title="设备与器械"
        subtitle={device.tips}
        loading={deviceLoading}
        error={deviceError}
        onRetry={() => setRetryKey((current) => current + 1)}
      >
        <div className={`${styles.kpiRow} ${styles.kpiRow2}`}>
          <StatCard
            title="设备总数"
            value={asset.deviceTotal}
            unit="台"
            hint={asset.deviceTotalHint}
          />
          <StatCard
            title="使用中设备"
            value={asset.deviceActive}
            unit="台"
            hint={asset.deviceActiveHint}
          />
        </div>
      </SectionCard>

      <div className={styles.chartRow}>
        <SectionCard title="设备使用热度 TOP6" subtitle="近 30 天碰一碰次数">
          <ChartCard height={280} empty={!(device.hotTop6 || []).length}>
            <Bar
              height={280}
              data={device.hotTop6 || []}
              xField="value"
              yField="name"
              legend={false}
            />
          </ChartCard>
        </SectionCard>

        <SectionCard
          title="设备使用时间统计"
          subtitle="近 24 小时各时段碰一碰次数"
        >
          <ChartCard height={280} empty={!(hourUsage.labels || []).length}>
            <Column
              height={280}
              data={(hourUsage.labels || []).map((l, i) => ({
                hour: `${l}:00`,
                value: (hourUsage.value || [])[i] || 0,
              }))}
              xField="hour"
              yField="value"
              legend={false}
            />
          </ChartCard>
        </SectionCard>
      </div>

      <SectionCard title="设备资产盘点明细" subtitle="当前商家已录入的设备资产">
        <DataTable
          size="small"
          rowKey={(record, index) =>
            record.id ||
            record.snCode ||
            `${record.equipmentId || 'device'}-${record.ctime || index}`
          }
          columns={storeDeviceColumns}
          dataSource={assetPage.data}
          emptyText="暂无设备资产"
          emptyHint="设备录入后会显示在这里"
          pagination={{
            current: assetPage.currentPage,
            pageSize: assetPage.pageSize,
            total: assetPage.total || 0,
            showSizeChanger: false,
            onChange: (currentPage, pageSize) =>
              setAssetPage((current) => ({
                ...current,
                currentPage,
                pageSize,
              })),
          }}
        />
      </SectionCard>
    </div>
  );
};

export default StoreOps;
